import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http, currency } from "@/lib/api";
import { CheckCircle, ArrowRight, Spinner } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, error: "Missing session id" }); return; }
    let alive = true;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, data });
          return;
        }
        if (tries++ > 20) { setState({ loading: false, data, error: "Still pending — check again later." }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, error: "Could not verify payment." });
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center grain relative p-6">
      <div className="border border-border bg-card p-10 max-w-md w-full text-center" data-testid="payment-success">
        {state.loading ? (
          <>
            <Spinner size={40} className="mx-auto animate-spin text-primary" />
            <div className="uppercase-label mt-4">Confirming payment</div>
            <h1 className="font-display text-2xl font-bold mt-2">Verifying with Stripe…</h1>
          </>
        ) : state.error ? (
          <>
            <div className="uppercase-label text-[hsl(var(--warning))]">Awaiting confirmation</div>
            <h1 className="font-display text-2xl font-bold mt-2">Payment status pending</h1>
            <p className="text-sm text-muted-foreground mt-2">{state.error}</p>
          </>
        ) : (
          <>
            <CheckCircle size={48} weight="fill" className="mx-auto text-[hsl(var(--success))]" />
            <div className="uppercase-label mt-4">Payment received</div>
            <h1 className="font-display text-2xl font-bold mt-2">Thank you!</h1>
            <div className="mt-4 font-mono text-sm space-y-1">
              <div className="text-muted-foreground">Invoice</div>
              <div>{state.data?.invoice_no}</div>
              <div className="text-muted-foreground mt-2">Amount</div>
              <div>{currency(state.data?.amount)}</div>
            </div>
          </>
        )}
        <Link to="/billing" className="mt-6 inline-flex items-center gap-2 text-xs font-mono text-primary hover:underline" data-testid="back-billing">
          back to billing <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
